import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/utils/tailwind";

import { InlineText } from "./inline-text";

interface LearningCatalogProps {
  children: ReactNode;
  countLabel: string;
  label: string;
}

// Provide the shared frame used by the course and concept indexes.
//
export function LearningCatalog({
  children,
  countLabel,
  label,
}: LearningCatalogProps) {
  return (
    <div className="bg-card/90 overflow-hidden rounded-md border shadow-sm">
      <div className="bg-muted/30 flex items-center justify-between gap-3 border-b px-4 py-3">
        <p className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
          {label}
        </p>
        <p className="text-muted-foreground font-mono text-xs">{countLabel}</p>
      </div>
      <div className="grid">{children}</div>
    </div>
  );
}

interface LearningCatalogSectionHeaderProps {
  description: string;
  eyebrow: string;
  meta: string;
  title: string;
  trailing?: ReactNode;
}

// Keep group hierarchy consistent while allowing view-specific behavior.
//
export function LearningCatalogSectionHeader({
  description,
  eyebrow,
  meta,
  title,
  trailing,
}: LearningCatalogSectionHeaderProps) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-muted-foreground font-mono text-xs">{eyebrow}</p>
          <p className="text-muted-foreground font-mono text-xs">{meta}</p>
        </div>
        <div className="grid gap-1">
          <h2 className="text-base font-semibold">
            <InlineText text={title} />
          </h2>
          <p className="text-muted-foreground max-w-3xl text-sm leading-6">
            {description}
          </p>
        </div>
      </div>
      {trailing && (
        <span className="text-muted-foreground mt-1">{trailing}</span>
      )}
    </div>
  );
}

interface LearningCatalogItemProps {
  accessBadge?: ReactNode;
  ariaLabel?: string;
  description?: string;
  eyebrow: string;
  href: string;
  isMuted?: boolean;
  marker: ReactNode;
  metadata?: readonly string[];
  title: string;
  trailing: ReactNode;
}

// Render one shared learning row with view-specific metadata and state.
//
export function LearningCatalogItem({
  accessBadge,
  ariaLabel,
  description,
  eyebrow,
  href,
  isMuted = false,
  marker,
  metadata = [],
  title,
  trailing,
}: LearningCatalogItemProps) {
  return (
    <li>
      <Link
        aria-label={ariaLabel}
        to={href}
        className={cn(
          "hover:bg-accent/70 group grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-2 py-3 transition sm:grid-cols-[2.5rem_minmax(0,1fr)_auto_auto]",
          isMuted && "text-muted-foreground",
        )}
      >
        <span
          className={cn(
            "bg-secondary text-secondary-foreground ring-border/70 flex size-9 items-center justify-center rounded-md font-mono text-[11px] shadow-xs ring-1",
            isMuted && "text-secondary-foreground/60 ring-border/50",
          )}
        >
          {marker}
        </span>

        <span className="grid min-w-0 gap-1">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-muted-foreground font-mono text-[11px]">
              {eyebrow}
            </span>
            {accessBadge}
          </span>
          <span className="font-medium text-pretty">
            <InlineText text={title} />
          </span>
          {description && (
            <span className="text-muted-foreground line-clamp-2 text-sm leading-6">
              {description}
            </span>
          )}
          {metadata.length > 0 && (
            <span className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs sm:hidden">
              {metadata.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </span>
          )}
        </span>

        {metadata.length > 0 && (
          <span className="text-muted-foreground hidden flex-wrap justify-end gap-x-4 gap-y-1 text-right text-xs sm:flex">
            {metadata.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </span>
        )}

        <span className="text-muted-foreground group-hover:text-foreground transition-transform group-hover:translate-x-0.5">
          {trailing}
        </span>
      </Link>
    </li>
  );
}
